import { useCallback, useRef, useState } from 'react'
import { CleoMessage, SSEEvent } from '../../../../models/cleo.model'
import { sendMessageSSE } from '../../../../services/cleo.service'

export type ChatStatus = 'idle' | 'streaming' | 'tool_processing' | 'error'

interface UseSSEChatReturn {
    messages: CleoMessage[]
    setMessages: React.Dispatch<React.SetStateAction<CleoMessage[]>>
    status: ChatStatus
    sendMessage: (conversationId: number, content: string) => Promise<void>
    cancel: () => void
}

export function useSSEChat(onComplete?: () => void): UseSSEChatReturn {
    const [messages, setMessages] = useState<CleoMessage[]>([])
    const [status, setStatus] = useState<ChatStatus>('idle')
    const abortRef = useRef<AbortController | null>(null)

    const cancel = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort()
            abortRef.current = null
        }
        setStatus('idle')
    }, [])

    const sendMessage = useCallback(async (conversationId: number, content: string) => {
        cancel()

        const userMessage: CleoMessage = { role: 'user', content }
        setMessages(prev => [...prev, userMessage])

        const assistantMessage: CleoMessage = { role: 'assistant', content: '', isStreaming: true }
        setMessages(prev => [...prev, assistantMessage])
        setStatus('streaming')

        const controller = new AbortController()
        abortRef.current = controller

        try {
            const response = await sendMessageSSE(conversationId, content, controller.signal)

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`)
            }

            const reader = response.body?.getReader()
            if (!reader) throw new Error('No reader available')

            const decoder = new TextDecoder()
            let buffer = ''

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n\n')
                buffer = lines.pop() || ''

                for (const line of lines) {
                    const dataLine = line.trim()
                    if (!dataLine.startsWith('data: ')) continue

                    try {
                        const event: SSEEvent = JSON.parse(dataLine.slice(6))

                        switch (event.type) {
                            case 'chunk':
                                setMessages(prev => {
                                    const updated = [...prev]
                                    const last = updated[updated.length - 1]
                                    if (last && last.role === 'assistant') {
                                        updated[updated.length - 1] = {
                                            ...last,
                                            content: last.content + (event.content || ''),
                                            isStreaming: true,
                                            isToolProcessing: false,
                                        }
                                    }
                                    return updated
                                })
                                break

                            case 'tool_calls':
                                setStatus('tool_processing')
                                setMessages(prev => {
                                    const updated = [...prev]
                                    const last = updated[updated.length - 1]
                                    if (last && last.role === 'assistant') {
                                        updated[updated.length - 1] = {
                                            ...last,
                                            toolCalls: event.tools,
                                            isToolProcessing: true,
                                        }
                                    }
                                    return updated
                                })
                                break

                            case 'tool_results':
                                setStatus('streaming')
                                setMessages(prev => {
                                    const updated = [...prev]
                                    const last = updated[updated.length - 1]
                                    if (last && last.role === 'assistant') {
                                        updated[updated.length - 1] = {
                                            ...last,
                                            content: '',
                                            isToolProcessing: false,
                                            isStreaming: true,
                                        }
                                    }
                                    return updated
                                })
                                break

                            case 'done':
                                setMessages(prev => {
                                    const updated = [...prev]
                                    const last = updated[updated.length - 1]
                                    if (last && last.role === 'assistant') {
                                        updated[updated.length - 1] = {
                                            ...last,
                                            id: event.messageId ?? last.id,
                                            isStreaming: false,
                                            isToolProcessing: false,
                                        }
                                    }
                                    return updated
                                })
                                setStatus('idle')
                                onComplete?.()
                                break

                            case 'error':
                                setMessages(prev => {
                                    const updated = [...prev]
                                    const last = updated[updated.length - 1]
                                    if (last && last.role === 'assistant') {
                                        updated[updated.length - 1] = {
                                            ...last,
                                            content: event.content || 'Erro ao processar sua mensagem. Tente novamente.',
                                            isStreaming: false,
                                            isToolProcessing: false,
                                        }
                                    }
                                    return updated
                                })
                                setStatus('error')
                                break
                        }
                    } catch {
                        // ignore malformed JSON
                    }
                }
            }
        } catch (err: any) {
            if (err.name === 'AbortError') return

            setMessages(prev => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = {
                        ...last,
                        content: 'Erro de conexão. Verifique sua internet e tente novamente.',
                        isStreaming: false,
                        isToolProcessing: false,
                    }
                }
                return updated
            })
            setStatus('error')
        }
    }, [cancel, onComplete])

    return { messages, setMessages, status, sendMessage, cancel }
}
